import { useTranslation } from 'react-i18next';
import { Redirect, Route, Switch } from 'react-router-dom';

import { useFeature, useViewerRoles } from '@treams/treams-hooks';
import * as graphql from '@treams/treams-network';
import { Container, Jumbo, Tabs, tokens } from '@treams/treams-ui';

import CareerEdit, { CareerCreator } from './growth-path/components/CareerEdit';
import CareersSettings from './growth-path/components/CareersSettings';
import JobLevelEditorWithID, {
	JobLevelEditor as JobLevelCreator,
} from './job-levels/JobLevelEditor';
import JobLevelsListContainer from './job-levels/JobLevelsListContainer';
import JobTitleEditorWithID, {
	JobTitleEditor as JobTitleCreator,
} from './job-titles/JobTitleEditor';
import JobTitlesListContainer from './job-titles/JobTitlesListContainer';
import SalaryScaleEditorWithID, {
	SalaryScaleEditor as SalaryScaleCreator,
} from './salary-scales/SalaryScaleEditor';
import SalaryScalesListContainer from './salary-scales/SalaryScalesListContainer';

export default function OrganizationSettings() {
	const { t } = useTranslation();
	const { canViewAndEditAllSettings } = useViewerRoles();

	const hasGrowthPath = useFeature(graphql.FeatureName.GrowPath);
	const hasRewards = useFeature(graphql.FeatureName.Rewards);

	return (
		<>
			<Jumbo
				title={t('settings.jobs.title')}
				description={t('settings.jobs.description')}
				footer={
					<Tabs.Container mb="-1px">
						<Tabs.Item
							title={t('settings.jobs.titles.title')}
							path={['/settings/jobs/titles', '/settings/jobs/titles/:id']}
						/>
						{hasRewards && (
							<Tabs.Item
								title={t('settings.jobs.salaryScales.title')}
								path={['/settings/jobs/salary-scales', '/settings/jobs/salary-scales/:id']}
							/>
						)}
						<Tabs.Item
							title={t('settings.jobs.levels.title')}
							path={['/settings/jobs/levels', '/settings/jobs/levels/:id']}
						/>
						{hasGrowthPath && canViewAndEditAllSettings && (
							<Tabs.Item
								title={t('settings.growthPath.title')}
								path={['/settings/jobs/growth-path', '/settings/jobs/growth-path/:id']}
							/>
						)}
					</Tabs.Container>
				}
			/>
			<Container pt={`${tokens.gap.page}px`} pb={4}>
				<Switch>
					<Route path="/settings/jobs/titles/new">
						<JobTitleCreator />
					</Route>
					<Route path="/settings/jobs/titles/:id">
						{({ match }) => <JobTitleEditorWithID id={match!.params.id!} />}
					</Route>
					<Route path="/settings/jobs/titles">
						<JobTitlesListContainer />
					</Route>

					{hasRewards && (
						<Route path="/settings/jobs/salary-scales/new">
							<SalaryScaleCreator />
						</Route>
					)}
					{hasRewards && (
						<Route path="/settings/jobs/salary-scales/:id">
							{({ match }) => <SalaryScaleEditorWithID id={match!.params.id!} />}
						</Route>
					)}
					{hasRewards && (
						<Route path="/settings/jobs/salary-scales">
							<SalaryScalesListContainer />
						</Route>
					)}

					<Route path="/settings/jobs/levels/new">
						<JobLevelCreator />
					</Route>
					<Route path="/settings/jobs/levels/:id">
						{({ match }) => <JobLevelEditorWithID id={match!.params.id!} />}
					</Route>
					<Route path="/settings/jobs/levels">
						<JobLevelsListContainer />
					</Route>

					<Route path="/settings/jobs/growth-path/new">
						<CareerCreator />
					</Route>
					<Route path="/settings/jobs/growth-path/:id">
						{({ match }) => <CareerEdit id={match!.params.id!} />}
					</Route>
					<Route path="/settings/jobs/growth-path">
						<CareersSettings />
					</Route>

					<Route>
						<Redirect to="/settings/jobs/titles" />
					</Route>
				</Switch>
			</Container>
		</>
	);
}
